import React, {
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useRef,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { Box } from '@apia/theme';
import uniqueId from 'lodash-es/uniqueId';
import { useCombinedRefs } from '@apia/util';
import { getVariant } from '@apia/theme';
import { Overlay } from './Overlay';
import DialogHeader from './layout/DialogHeader';
import { ModalContext } from './ModalContext';
import { TModal } from './Modal';
import { useEscapeKey } from './hooks/useEscapeKey';
import { useInitialFocus } from './hooks/useInitialFocus';
import { resetModalsPositions } from './windowModal/bringToTop';

let canCloseOnClick = true;

export const StaticModal = forwardRef(
  (
    {
      animationDuration = 150,
      children = null,
      className,
      hideCloseButton,
      id,
      initialFocusConfiguration,
      initialFocusGetter,
      initialFocusRef,
      isOpen = false,
      maxWidth,
      NavBar,
      noFocus,
      noHeader = false,
      onClose = () => {},
      onExited = () => {},
      Portal,
      shouldCloseOnEsc = true,
      shouldCloseOnOverlayClick = true,
      size = 'md',
      stretch,
      title = '',
      variant,
    }: Omit<TModal, 'asWindow'>,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const lastTitle = useRef(title);
    const [innerTitle, setInnerTitle] = useState(title);
    if (lastTitle.current !== title && innerTitle !== title) {
      setInnerTitle(title);
    }
    const [isOpenInner, setIsOpen] = useState(isOpen);
    const innerId = React.useMemo(() => {
      return `Modal${uniqueId()}`;
    }, []);

    useEffect(() => {
      setIsOpen(isOpen);
    }, [isOpen]);

    const closeHandler = useCallback(() => {
      setIsOpen(false);
      onClose();
    }, [onClose]);
    const [overlayElement, setInnerRef] = React.useState<HTMLElement>();
    const assignRefs = useCombinedRefs(
      setInnerRef as unknown as React.ForwardedRef<unknown>,
      ref as unknown as React.Ref<unknown>,
      ((el: HTMLElement) => {
        if (el) {
          resetModalsPositions();
        }
      }) as unknown as React.Ref<unknown>,
    );

    const overlayClickedHandle = (event: React.MouseEvent<HTMLDivElement>) => {
      if (shouldCloseOnOverlayClick && canCloseOnClick) {
        if (
          (event.target as HTMLDivElement).getAttribute('data-type') ===
          'overlay'
        ) {
          closeHandler();
        }
      }

      canCloseOnClick = true;
    };

    useEscapeKey({ closeHandler, id: innerId, isOpenInner, shouldCloseOnEsc });
    useInitialFocus({
      isOpenInner,
      initialFocusConfiguration,
      initialFocusGetter,
      initialFocusRef,
      noFocus,
      parentElement: overlayElement,
    });

    const Wrapper = React.useMemo(() => {
      return Portal ?? React.Fragment;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nodeRef = useRef<HTMLElement | null>(null);
    const uns = useRef(() => {});

    return (
      <ModalContext.Provider
        value={React.useMemo(() => {
          return {
            changeTitle: (newTitle) => {
              setInnerTitle(newTitle);
            },
            close: () => {
              closeHandler();
            },
            descriptionId: `${innerId}_Description`,
          };
        }, [closeHandler, innerId])}
      >
        <Wrapper>
          <Overlay
            onOverlayClicked={overlayClickedHandle}
            isOpen={isOpen}
            className={className}
            stretch={stretch}
            ref={assignRefs}
            onExited={onExited}
            id={innerId}
            shouldAvoidInitialFocusTrap={noFocus}
          >
            <CSSTransition
              in={isOpen}
              timeout={animationDuration}
              classNames="modal"
              appear
              unmountOnExit
              nodeRef={nodeRef}
            >
              <Box
                ref={(el) => {
                  nodeRef.current = el;
                  uns.current();
                  if (el) {
                    const handleMouseDown = () => {
                      canCloseOnClick = false;
                    };
                    const handleMouseUp = () => {
                      canCloseOnClick = true;
                    };
                    el.addEventListener('mousedown', handleMouseDown);
                    uns.current = () =>
                      el.removeEventListener('mousedown', handleMouseDown);
                    el.addEventListener('mouseup', handleMouseUp);
                    uns.current = () =>
                      el.removeEventListener('mouseup', handleMouseUp);
                  }
                }}
                className="modal__variantWrapper"
                {...(variant ? getVariant(variant) : {})}
              >
                <Box
                  id={id}
                  role="dialog"
                  aria-modal="true"
                  aria-label={title}
                  aria-describedby={`${innerId}_Description`}
                  aria-hidden="false"
                  className="modal__main"
                  // allows scrolling within element, despite body lock
                  data-scroll-lock-scrollable
                  sx={React.useMemo(() => {
                    return {
                      variant: `layout.common.modals.${size}`,
                      ...(maxWidth !== undefined ? { maxWidth } : {}),
                    };
                  }, [maxWidth, size])}
                  data-variant={`layout.common.modals.${size}`}
                >
                  {!noHeader && (
                    <DialogHeader
                      className="modal__header"
                      NavBar={NavBar}
                      title={innerTitle}
                      close={closeHandler}
                      hideCloseButton={hideCloseButton}
                      id={`${innerId}_Header`}
                    />
                  )}
                  <Box
                    className="modal__content"
                    id={
                      typeof children === 'string'
                        ? `${innerId}_Description`
                        : undefined
                    }
                  >
                    {children}
                  </Box>
                </Box>
              </Box>
            </CSSTransition>
          </Overlay>
        </Wrapper>
      </ModalContext.Provider>
    );
  },
);

StaticModal.displayName = 'StaticModal';
